// SignatureDialog (Parte 14.2): mostra o que está sendo assinado (resumo + hash), o significado (seleção fechada)
// e conclui com step-up. Esqueleto M0 — a assinatura avançada chega no M7 (GIY-085).
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  NativeSelect,
} from '@giyus/ui';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SIGNATURE_MEANINGS, type SignatureMeaning } from './signatureMeanings';

export interface SignatureDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Resumo legível do objeto assinado. */
  readonly summary: string;
  /** Hash do conteúdo (exibido tal qual). */
  readonly contentHash: string;
  readonly meanings?: readonly SignatureMeaning[];
  readonly onSign: (meaning: SignatureMeaning) => void | Promise<void>;
}

export function SignatureDialog({
  open,
  onOpenChange,
  summary,
  contentHash,
  meanings = SIGNATURE_MEANINGS,
  onSign,
}: SignatureDialogProps) {
  const { t } = useTranslation('common');
  const [meaning, setMeaning] = useState<SignatureMeaning | ''>('');
  const selectId = useId();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('a11y.closeDialog')}>
        <DialogHeader>
          <DialogTitle>{t('components.signature.title')}</DialogTitle>
          <DialogDescription>{t('components.signature.description')}</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-1 gap-2 text-body">
          <dt className="font-medium">{t('components.signature.summaryLabel')}</dt>
          <dd data-testid="signature-summary">{summary}</dd>
          <dt className="font-medium">{t('components.signature.hashLabel')}</dt>
          <dd className="font-mono text-caption break-all" data-testid="signature-hash">
            {contentHash}
          </dd>
        </dl>
        <label htmlFor={selectId} className="text-body font-medium">
          {t('components.signature.meaningLabel')}
        </label>
        <NativeSelect
          id={selectId}
          value={meaning}
          onChange={(event) => {
            setMeaning(event.target.value as SignatureMeaning | '');
          }}
        >
          <option value="">{t('components.signature.meaningPlaceholder')}</option>
          {meanings.map((item) => (
            <option key={item} value={item}>
              {t(`components.signature.meanings.${item}`)}
            </option>
          ))}
        </NativeSelect>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('actions.cancel')}
          </Button>
          <Button
            disabled={meaning === ''}
            onClick={() => {
              if (meaning !== '') {
                void onSign(meaning);
              }
            }}
          >
            {t('components.signature.sign')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
